/**
 * LoginPropositions — the three value-proposition lines plus the "Learn more"
 * affordance shown above the login provider buttons.
 *
 * Flutter source of truth: `LoginScreen.propositions()`
 * (`lib/presentation/screens/login/login_screen.dart:282-335`):
 *   - An `AnimatedOpacity` (`:283-285`, `duration: 1000ms`, `opacity: _textOpacity`)
 *     wraps a centered `Column` holding BOTH the three `Text`s and the
 *     "Learn more" control — the button fades together with the copy, it is not
 *     a sibling of the animation.
 *   - The fade is driven by `double _textOpacity = 0` (`:42`) flipped to `1`
 *     inside `addPostFrameCallback` (`:51-56` / `:58-63`), i.e. strictly AFTER
 *     the first frame is committed. The web mirrors that with a
 *     `requestAnimationFrame` flip (see below) — a bare `setState` in a mount
 *     effect can be batched into the first paint, and the transition would then
 *     never run at all.
 *   - The three lines (`:290-316`): `fontSize: 24.0`,
 *     `color: AppColors.disabledTextColor`; only the middle one carries
 *     `EdgeInsets.only(top: 4.0, bottom: 4)` (`:298-299`), i.e. a 4px gap
 *     between the lines and none anywhere else.
 *   - "Learn more" (`:317-331`): an `InkWell` with every ripple/highlight/splash
 *     colour set to transparent (a flat, non-rippling text control),
 *     `onTap: NavigationService.navigateToAboutScreen()`, child
 *     `Container(padding: EdgeInsets.all(12.0))` with
 *     `AppStyles.notificationsLoadMoreStyle` (`app_styles.dart:1394-1399`:
 *     `AppColors.more`/blueAccent, size 14, weight 400).
 *
 * Intentional web mappings (UI idiom may deviate from Flutter, business
 * behaviour may not):
 *   - Flutter `AppColors.disabledTextColor` → `theme.text.secondary`.
 *   - Flutter's transparent-ripple `InkWell` → the shared `ExpauseButton` with
 *     `ButtonVariant.text`, following the `MoreNotificationsRow` precedent
 *     (the web port of this same `notificationsLoadMoreStyle`): default colour
 *     `theme.blue` is the project's `AppColors.more`/blueAccent mapping, at
 *     `responsive.font.sm` + `responsive.fontWeight.regular`.
 *   - The column `gap` also inserts 4px between "Top monetization" and
 *     "Learn more", where Flutter has 0. Accepted rather than chased with a
 *     per-child override: it replaces a previously much larger 12px gap, so the
 *     block tightens toward Flutter rather than away from it.
 *
 * This component paints NO outer margin — the section spacing between the
 * logo / propositions / buttons is owned by `LoginPage`.
 */
import { useEffect, useState } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseButton, ButtonVariant } from '@/presentation/components/common/controls/ExpauseButton';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { Navigator } from '@/presentation/routing/Navigator';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const LoginPropositions = () => {
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Flutter `_textOpacity` 0 → 1 (`login_screen.dart:42`, flipped at `:55` /
  // `:61-63` from the post-first-frame callback). The `requestAnimationFrame`
  // is load-bearing: it guarantees the browser commits the `opacity: 0` frame
  // before the flip, so the CSS transition actually runs.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const propositionStyle = {
    margin: 0,
    fontSize: responsive.font['3xl'],
    color: theme.text.secondary,
  };

  return (
    <div
      {...qaAttr({ id: 'login-propositions', status: visible ? 'visible' : 'hidden' })}
      style={{
        opacity: visible ? 1 : 0,
        // `linear` is Flutter's `AnimatedOpacity` default curve.
        transition: `opacity ${ANIMATION_CONSTANTS.duration.extraLong}ms ${ANIMATION_CONSTANTS.easing.linear}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: responsive.gap.xs,
        textAlign: 'center',
      }}
    >
      <p style={propositionStyle}>{l10n.auth.propositionExclusiveContent}</p>
      <p style={propositionStyle}>{l10n.auth.propositionLiveStreaming}</p>
      <p style={propositionStyle}>{l10n.auth.propositionTopMonetization}</p>

      {/* Inside the faded container on purpose — Flutter fades this control too
          (it is a child of the same `AnimatedOpacity`). Keeping the transition
          on the wrapper also avoids fighting `ExpauseButton`'s own
          `transition-colors` / `hover:opacity-80` classes. */}
      <ExpauseButton
        {...qaAttr({ id: 'login-learn-more' })}
        variant={ButtonVariant.text}
        textColor={theme.blue}
        onClick={() => Navigator.navigateToAbout()}
        style={{
          fontSize: responsive.font.sm,
          fontWeight: responsive.fontWeight.regular,
          // Spread last inside `ExpauseButton`, so this beats the text
          // variant's `padding: 0` — Flutter's `EdgeInsets.all(12.0)`.
          padding: responsive.spacing.base,
          fontFamily: 'inherit',
        }}
      >
        {l10n.auth.learnMore}
      </ExpauseButton>
    </div>
  );
};
